import {
  LayoutAlignmentOptions,
  LayoutCaptionAlignmentOptions,
  LayoutNumberOfColumnsOptions,
  layoutSchema,
} from "@/lib/cms/section-types";
import { cn } from "@/lib/utils";
import {
  AlignCenter,
  AlignCenterHorizontal,
  AlignHorizontalSpaceAround,
  AlignLeft,
  AlignRight,
  AlignStartHorizontal,
  Columns2,
  Columns3,
  Columns4,
} from "lucide-react";
import React, { cloneElement } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type LayoutSelectorProps<T, FV extends FieldValues = FieldValues> = {
  label: string;
  content: {
    icon: React.ReactElement;
    tooltip?: string;
  }[];
  valueOptions: T[];
  name: Path<FV>;
  control: Control<FV>;
};

export const LayoutSelector = <
  T extends string | number,
  FV extends FieldValues,
>({
  label,
  content,
  valueOptions,
  name,
  control,
}: LayoutSelectorProps<T, FV>) => {
  if (content.length !== valueOptions.length) {
    throw new Error(
      "LayoutSelector: Icons and valueOptions should have the same length. Got icons.length=" +
        content.length +
        " and valueOptions.length=" +
        valueOptions.length,
    );
  }

  return (
    <div className="flex flex-col gap-1.5 w-[30%] items-center">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const selectedIndex = valueOptions.indexOf(field.value);
          return (
            <TooltipProvider>
              <div className="flex flex-row">
                {content.map(({ icon, tooltip }, index) => {
                  const styledIcon = cloneElement(icon, {
                    color: selectedIndex === index ? "black" : "gray",
                  });

                  const button = (
                    <button
                      type="button"
                      key={index}
                      onClick={() => field.onChange(valueOptions[index])}
                      className={cn(
                        `px-3 py-2 border border-gray-300 transition-colors ${
                          index === 0
                            ? "rounded-l-lg"
                            : index === content.length - 1
                              ? "rounded-r-lg"
                              : ""
                        } ${selectedIndex === index && "bg-white"}`,
                      )}
                    >
                      {styledIcon}
                    </button>
                  );

                  return tooltip ? (
                    <Tooltip key={index}>
                      <TooltipTrigger asChild>{button}</TooltipTrigger>
                      <TooltipContent className="bg-background">
                        <p className="text-sm font-semibold text-black">
                          {tooltip}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    button
                  );
                })}
              </div>
            </TooltipProvider>
          );
        }}
      />
    </div>
  );
};

export const manageLayoutFormSchema = z.object({
  layout: layoutSchema,
});

export type ManageLayoutFormValues = z.infer<typeof manageLayoutFormSchema>;

type ManageLayoutSelectorFullMenuProps<
  FV extends ManageLayoutFormValues = ManageLayoutFormValues,
> = {
  control: Control<FV>;
};

export const ManageLayoutFullMenu = <FV extends ManageLayoutFormValues>({
  control,
}: ManageLayoutSelectorFullMenuProps<FV>) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row gap-6 justify-evenly">
      <LayoutSelector
        label={t("cms.selection.manageLayout.numberOfColumns")}
        content={[
          {
            icon: <Columns2 />,
            tooltip: t("cms.selection.manageLayout.numberOfColumns.2"),
          },
          {
            icon: <Columns3 />,
            tooltip: t("cms.selection.manageLayout.numberOfColumns.3"),
          },
          {
            icon: <Columns4 />,
            tooltip: t("cms.selection.manageLayout.numberOfColumns.4"),
          },
        ]}
        valueOptions={LayoutNumberOfColumnsOptions}
        name={"layout.numberOfColumns" as Path<FV>}
        control={control}
      />
      <LayoutSelector
        label={t("cms.selection.manageLayout.alignment")}
        content={[
          {
            icon: <AlignCenterHorizontal />,
            tooltip: t("cms.selection.manageLayout.alignment.center"),
          },
          {
            icon: <AlignStartHorizontal />,
            tooltip: t("cms.selection.manageLayout.alignment.start"),
          },
          {
            icon: <AlignHorizontalSpaceAround />,
            tooltip: t("cms.selection.manageLayout.alignment.spaceAround"),
          },
        ]}
        valueOptions={LayoutAlignmentOptions}
        name={"layout.alignment" as Path<FV>}
        control={control}
      />
      <LayoutSelector
        label={t("cms.selection.manageLayout.captionAlignment")}
        content={[
          {
            icon: <AlignLeft />,
            tooltip: t("cms.selection.manageLayout.captionAlignment.left"),
          },
          {
            icon: <AlignCenter />,
            tooltip: t("cms.selection.manageLayout.captionAlignment.center"),
          },
          {
            icon: <AlignRight />,
            tooltip: t("cms.selection.manageLayout.captionAlignment.right"),
          },
        ]}
        valueOptions={LayoutCaptionAlignmentOptions}
        name={"layout.captionAlignment" as Path<FV>}
        control={control}
      />
    </div>
  );
};
